/**
 * Команды замку и телеметрия.
 *
 * ⚠ ЭТО ЕДИНСТВЕННАЯ ЧАСТЬ ПРИЛОЖЕНИЯ, КОТОРАЯ НЕ РАБОТАЕТ БЕЗ СЕТИ, и не по
 * недосмотру. Команда идёт скаут → BFF → iot-gateway → живая TCP-сессия с
 * замком. Нет сети — некому донести команду до железа. В офлайн-очередь такое
 * класть НЕЛЬЗЯ: «открыть» через сорок минут, когда скаут уже уехал, — это
 * открытый бесхозный байк на улице.
 *
 * Отсюда правило интерфейса: в офлайне кнопки замка неактивны и честно
 * объясняют почему, а не притворяются принятыми.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * АДРЕСАЦИЯ — vehicle_id, А НЕ IMEI, И ГЕЙТ — РОЛЬ, А НЕ ВЗЯТАЯ ЗАДАЧА
 * ─────────────────────────────────────────────────────────────────────────
 * Раньше здесь были ручки `/v1/devices/{imei}/…`, и обе их особенности мешали
 * ровно в главном полевом случае. IMEI скаутская проекция парка не отдаёт
 * (`api/vehicles.ts`), поэтому позвать их можно было только по байку из
 * задачи. А гейт требовал ВЗЯТОЙ задачи на этот байк — то есть открыть багажник
 * у байка, задачи на который ещё нет, было нельзя. Между тем чтобы понять,
 * нужна ли задача, надо сначала подойти и посмотреть.
 *
 * Теперь это `/v1/scout/vehicles/{id}/…`: гейт по роли, IMEI резолвит BFF и
 * наружу не отдаёт.
 *
 * ⚠ BLE-ручки (`GetDeviceBLEMac`, `IssueBLEKey`, Фаза 2) в этот ярус НЕ
 * переехали и остаются за взятой задачей. Команда отсюда идёт через сервер и
 * оставляет строку в журнале команд; BLE-ключ открывает замок офлайн и не
 * оставляет ничего.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, NetworkError, request } from '@/lib/http'

/** Ответ на команду замку: public.proto DeviceCommandReply. */
const commandReply = z.object({ status: z.string().default('') })
export type CommandStatus = 'success' | 'failure' | 'key_error' | 'timeout' | 'unspecified'

/**
 * Итог команды человеческим языком.
 *
 * `timeout` — самый частый и самый неоднозначный: замок не ответил, но команда
 * могла и дойти. Поэтому текст не утверждает, что ничего не произошло.
 */
export function commandLabel(status: string): string {
  switch (status as CommandStatus) {
    case 'success':
      return 'Готово'
    case 'timeout':
      return 'Замок не ответил вовремя. Проверьте байк — команда могла дойти'
    case 'key_error':
      return 'Замок не принял ключ. Нужен техник'
    case 'failure':
      return 'Замок отказал'
    default:
      return 'Непонятный ответ замка'
  }
}

export function isCommandOk(status: string): boolean {
  return status === 'success'
}

/**
 * Состояние байка и его координаты одним ответом.
 *
 * Один опрос вместо двух: карточка всегда спрашивает и то и другое, а каждый
 * запрос — это радиообмен с замком (`ScoutVehicleDevice` в public.proto).
 *
 * ⚠ `locationValid` отделён от координат по той же причине, по какой
 * `runtimeStale` отделён от заряда: замок без спутников отвечает нулями, а
 * (0, 0) — это Гвинейский залив, а не «не знаю».
 */
const scoutDevice = z.object({
  batteryPct: z.number().default(0),
  locked: z.boolean().default(false),
  charging: z.boolean().default(false),
  signalLevel: z.number().default(0),
  totalMileage: z.string().default(''),
  locationValid: z.boolean().default(false),
  lat: z.number().default(0),
  lng: z.number().default(0),
  satellites: z.number().default(0),
})
export type ScoutDevice = z.infer<typeof scoutDevice>

export const deviceQK = {
  device: (vehicleId: string) => ['scout.device', vehicleId] as const,
  gpsRefresh: (vehicleId: string) => ['scout.device.gpsRefresh', vehicleId] as const,
}

function fetchScoutDevice(vehicleId: string): Promise<ScoutDevice> {
  return request(`/v1/scout/vehicles/${vehicleId}/device`, scoutDevice)
}

/**
 * Опрос байка по требованию.
 *
 * `enabled` выключает запрос, когда байк неизвестен или нет сети.
 * Автоматически не обновляем: каждый опрос — радиообмен с железом, а не
 * чтение из кэша.
 */
export function useScoutDevice(vehicleId: string, enabled: boolean) {
  return useQuery({
    queryKey: deviceQK.device(vehicleId),
    queryFn: () => fetchScoutDevice(vehicleId),
    enabled: enabled && vehicleId !== '',
    staleTime: 15_000,
    retry: false,
  })
}

/**
 * «Обновить GPS» в шапке карточки (§2.2.1) — тот же опрос, что
 * `useScoutDevice`, но под ОТДЕЛЬНЫМ ключом кэша.
 *
 * ⚠ ПОЧЕМУ НЕ ПЕРЕИСПОЛЬЗУЕТ `deviceQK.device`. Тот ключ держит
 * `bike-lock.tsx` для перепроверки замка после «Закрыть» (7-секундная
 * задержка). Опрос из шапки может идти секундами и минутами — это тот же
 * живой радиообмен с замком, а не чтение из кэша. Один ключ на оба означал
 * бы: пока висит долгий опрос из шапки, TanStack Query дедуплицирует повторный
 * `refetch()` перепроверки в ТОТ ЖЕ промис, и «замок не защёлкнулся»
 * прочлось бы из ответа, вообще не связанного с этой командой «Закрыть».
 * Разные кнопки — разные промисы.
 *
 * Всегда `enabled: false` — кнопка вызывает `refetch()` сама, автоопроса нет
 * по той же причине, что и у `useScoutDevice`.
 */
export function useScoutDeviceRefresh(vehicleId: string) {
  return useQuery({
    queryKey: deviceQK.gpsRefresh(vehicleId),
    queryFn: () => fetchScoutDevice(vehicleId),
    enabled: false,
    retry: false,
  })
}

export type DeviceCommand = 'unlock' | 'lock' | 'ring'

/**
 * Команда замку. Идёт напрямую, МИМО очереди — см. шапку файла.
 *
 * После открытия и закрытия сбрасываем кэш состояния: замок только что
 * поменял положение, и показывать прежнее — врать. После «пикнуть» не
 * сбрасываем: писк ничего не меняет.
 */
export function useScoutVehicleCommand(vehicleId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (cmd: DeviceCommand) => {
      const out = await request(
        `/v1/scout/vehicles/${vehicleId}/${cmd}`,
        commandReply,
        { method: 'POST', body: { id: vehicleId } },
      )
      return { cmd, status: out.status }
    },
    onSettled: (_data, _err, cmd) => {
      if (cmd !== 'ring') {
        void qc.invalidateQueries({ queryKey: deviceQK.device(vehicleId) })
      }
    },
  })
}

export type HeadlightState = 'on' | 'off'

/** Omni S7: headlight — 1=выключить, 2=включить. */
const HEADLIGHT_VALUE: Record<HeadlightState, number> = { off: 1, on: 2 }

/**
 * Тело команды фонарику.
 *
 * Остальные настройки машины (режим скорости, курок, задний фонарь) сюда не
 * попадают вовсе: скаутскому ярусу они не открыты, и команда «фонарик» не
 * имеет права заодно поменять режим скорости.
 */
export function headlightBody(vehicleId: string, next: HeadlightState) {
  return { id: vehicleId, headlight: HEADLIGHT_VALUE[next] }
}

/**
 * Команда фонарику. Тот же класс, что unlock/lock/ring — идёт напрямую,
 * МИМО очереди: это живая радиокоманда, а не бизнес-состояние, которое имеет
 * смысл доставить позже.
 */
export function useSetHeadlight(vehicleId: string) {
  return useMutation({
    mutationFn: async (next: HeadlightState) => {
      const out = await request(
        `/v1/scout/vehicles/${vehicleId}/headlight`,
        commandReply,
        { method: 'POST', body: headlightBody(vehicleId, next) },
      )
      return { next, status: out.status }
    },
  })
}

/**
 * Сирена (C3) — 60 с, тот же класс, что unlock/lock/ring/headlight: живая
 * радиокоманда через iot-gateway, идёт МИМО очереди. Смысла доставлять её
 * через сорок минут нет: скаут, разбирающийся с кражей, давно принял решение
 * без неё.
 *
 * Роуту ещё нет в проде (C3) — 404 читается тем же `commandErrorText`, что и
 * остальные команды скаутского яруса.
 */
export function useSiren(vehicleId: string) {
  return useMutation({
    mutationFn: (seconds: number) =>
      request(`/v1/scout/vehicles/${vehicleId}/siren`, commandReply, {
        method: 'POST',
        body: { id: vehicleId, seconds },
      }),
  })
}

/**
 * Последние точки байка (C3) — «куда он ушёл» при подозрении на кражу или
 * пропажу с парковки.
 *
 * Не через outbox: это чтение, не мутация. `retry: false` и без
 * автообновления — та же причина, что у `useLastDrop`: ручка новая, на
 * невыкаченном бэкенде отвечает 404, и незачем повторять это трижды на
 * каждом открытии карточки.
 */
const trailPointSchema = z.object({
  lat: z.number().default(0),
  lng: z.number().default(0),
  at: z.string().default(''),
  speedKmh: z.number().default(0),
})
export type TrailPoint = z.infer<typeof trailPointSchema>

const trailReply = z.object({ points: z.array(trailPointSchema).default([]) })

/**
 * `opts.refetchInterval` — только режим слежения («Определить местоположение»,
 * `useLocateTracking` ниже), по образцу `useScoutVehicles(pageSize, opts)`:
 * опциональный параметр поверх дефолтного поведения, остальные вызывающие
 * (история в bike-card.tsx, limit=20) его не передают и не задеты.
 */
export function useVehicleTrail(
  vehicleId: string,
  enabled: boolean,
  limit = 20,
  opts: { refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: ['scout.trail', vehicleId, limit],
    queryFn: async () =>
      (await request(`/v1/scout/vehicles/${vehicleId}/trail`, trailReply, { query: { limit } }))
        .points,
    enabled: enabled && vehicleId !== '',
    staleTime: 60_000,
    retry: false,
    ...opts,
  })
}

/**
 * Причина, по которой командой сейчас пользоваться нельзя. Null — можно.
 *
 * Причина осталась одна. Раньше их было две: «нет IMEI» отпало вместе с
 * переходом на адресацию по vehicle_id, а «нет взятой задачи» — вместе с
 * гейтом по роли.
 */
export function commandBlockedReason(online: boolean, vehicleId: string): string | null {
  if (!vehicleId) return 'Байк не опознан — команду некому адресовать'
  if (!online) return 'Нет сети. Команда идёт через сервер, доехать до замка ей сейчас нечем'
  return null
}

/** Человеческий текст ошибки команды. */
export function commandErrorText(e: unknown): string {
  if (e instanceof NetworkError) return 'Нет связи с сервером'
  if (e instanceof ApiError) {
    /*
     * 404 на этих ручках означает не «байк не найден», а «сервер не знает
     * такого маршрута»: скаутский ярус выкачен позже клиента. Английский
     * текст Kratos в этом месте читается как поломка приложения, хотя
     * приложение исправно. Ветка снимается вместе с выкатом.
     */
    if (e.status === 404) return 'Сервер пока не умеет эту команду — ждём выката'
    // Прийти сюда SCOUT_TASK_REQUIRED больше не может: гейт по роли. Ветка
    // оставлена ради BLE-ручек Фазы 2, которые останутся за задачей.
    if (e.reason === 'SCOUT_TASK_REQUIRED') return 'Этот байк не в вашей задаче'
    if (e.reason === 'STAFF_REQUIRED') return 'Недостаточно прав для управления замком'
    /*
     * Единственный отказ, который защищает не парк, а человека: байк под
     * клиентом или в брони, и команда замка ушла бы едущему. Позвать
     * диспетчера тут не выход — ждать надо конца поездки.
     */
    if (e.reason === 'VEHICLE_IN_USE') return 'Байк под клиентом — замок трогать нельзя'
    if (e.reason === 'DEVICE_OFFLINE') return 'Замок не на связи — команду доставить некому'
    return e.message
  }
  return 'Команда не выполнена'
}

/**
 * «Определить местоположение» (рядом с «Обновить GPS», §2.2.1) — живой POST,
 * МИМО очереди, тот же класс, что unlock/lock/ring/siren выше: включает на
 * замке частый GPS на несколько минут и отдаёт первую точку.
 *
 * ⚠ `valid` — то же решение, что `locationValid` у ScoutDevice: без
 * спутников замок отвечает нулями, а (0, 0) не должно читаться как точка.
 * `trackingUntil` пустой — режим слежения не включился (например, велик в
 * аренде) — это единственный ответ, который будет.
 */
export const scoutLocateSchema = z.object({
  valid: z.boolean().default(false),
  lat: z.number().default(0),
  lng: z.number().default(0),
  satellites: z.number().default(0),
  trackingUntil: z.string().default(''),
})
export type LocateResult = z.infer<typeof scoutLocateSchema>

/**
 * Секунд до конца окна частого GPS. Пустая строка, непарсящаяся дата и уже
 * прошедший момент не различаются и все дают 0 — тот же принцип, что у
 * `timeAgo` в `lib/geo.ts` («Invalid Date» на экране хуже отсутствия строки):
 * вызывающему нужно только «продолжать опрашивать или нет».
 */
export function trackingSecondsLeft(trackingUntil: string, nowMs: number = Date.now()): number {
  if (trackingUntil === '') return 0
  const until = new Date(trackingUntil).getTime()
  if (Number.isNaN(until)) return 0
  return Math.max(0, Math.round((until - nowMs) / 1000))
}

/**
 * «Точка N с назад» для бейджа слежения — та же секундная точность и то же
 * округление, что у приватной `gpsAgoText` в `bike-header.tsx`. Не
 * переиспользуем её напрямую: она приватна компоненту, а `api/` не должен
 * зависеть от `components/`.
 */
function formatPointAge(updatedAt: number, nowMs: number): string | null {
  if (updatedAt === 0) return null
  const sec = Math.max(0, Math.round((nowMs - updatedAt) / 1000))
  return sec < 60 ? `точка ${sec} с назад` : `точка ${Math.floor(sec / 60)} мин назад`
}

/**
 * Текст отказа «Определить местоположение». Не `commandErrorText`: там
 * DEVICE_OFFLINE и обрыв сети НАРОЧНО звучат по-разному (замок vs сервер) —
 * команде замку это различие важно для скаута. Здесь нужен только
 * диагностический факт «устройство не ответило», источник не важен.
 */
export function locateErrorText(e: unknown): string {
  if (e instanceof ApiError && e.status === 404) return 'Появится после выката'
  if (e instanceof NetworkError) return 'Устройство не ответило'
  if (e instanceof ApiError && e.reason === 'DEVICE_OFFLINE') return 'Устройство не ответило'
  if (e instanceof ApiError) return e.message
  return 'Устройство не ответило'
}

/** Как часто опрашивать /device и /trail в режиме слежения. */
const LOCATE_TRACK_POLL_MS = 10_000
/** Последних точек достаточно для короткой линии на мини-карте карточки. */
const LOCATE_TRACK_TRAIL_LIMIT = 10

export type LocateTracking = {
  /** Запустить (или перезапустить) слежение. */
  start: () => void
  /** Идёт POST /locate. */
  starting: boolean
  /** Текст отказа последнего POST /locate, либо null. */
  error: string | null
  /** Слежение идёт: countdown > 0 и не остановлено «Стопом». */
  active: boolean
  secondsLeft: number
  satellites: number
  pointAgoText: string | null
  trail: readonly TrailPoint[] | undefined
  /** Останавливает опрос в UI. На сервере частый GPS доживает свой TTL сам. */
  stop: () => void
}

/**
 * «Определить местоположение» + режим слежения, пока `trackingUntil` из
 * ответа не истёк.
 *
 * ⚠ ОПРОС УСТРОЙСТВА ВО ВРЕМЯ СЛЕЖЕНИЯ ПИШЕТ В ТОТ ЖЕ КЛЮЧ, ЧТО
 * `useScoutDeviceRefresh` (`deviceQK.gpsRefresh`), А НЕ В СВОЙ ОТДЕЛЬНЫЙ. Это
 * не тот случай, ради которого `gpsRefresh` заведён отдельно от
 * `deviceQK.device` (см. комментарий там про перепроверку в `bike-lock.tsx`
 * после «Закрыть»): там два вызывающих отвечают на РАЗНЫЕ вопросы —
 * «защёлкнулся ли именно этот замок» vs «что вообще с устройством», — и
 * разделение спасает от взаимного искажения. Здесь оба вызывающих, «Обновить
 * GPS» и слежение, отвечают на ОДИН И ТОТ ЖЕ вопрос для одной и той же шапки
 * карточки, поэтому общий ключ — то, ради чего он и берётся: bike-card.tsx
 * получает свежие координаты/заряд через уже существующий `gpsRefresh.data`
 * и один-единственный вызов `applyDeviceReading`, без второго конкурирующего
 * источника истины. Осознанное следствие: пока идёт слежение, кнопка
 * «Обновить GPS» в шапке тоже может показать «Опрашиваем…» или отказ от
 * ЕГО опроса — это тот же самый вопрос «что сейчас говорит устройство»,
 * поэтому её busy/error-состояние законно смешивается со слежением.
 *
 * ⚠ `startedFor` ПРИВЯЗЫВАЕТ СЛЕЖЕНИЕ К БАЙКУ, КОТОРЫЙ ЕГО ЗАПУСТИЛ.
 * `/bike/:id` — один элемент маршрута: переход на другой байк меняет `id`,
 * но НЕ размонтирует `BikeCardPage` (React Router так не работает), а
 * значит не размонтирует и этот хук. Без `startedFor` обратный отсчёт и
 * опрос от байка A продолжили бы идти на карточке байка B — чужой таймер,
 * бьющий по чужому устройству. `active` требует совпадения `startedFor` с
 * ТЕКУЩИМ `vehicleId`, поэтому смена байка гасит слежение немедленно.
 */
export function useLocateTracking(vehicleId: string): LocateTracking {
  const locate = useMutation({
    mutationFn: () =>
      request(`/v1/scout/vehicles/${vehicleId}/locate`, scoutLocateSchema, {
        method: 'POST',
        body: { id: vehicleId },
      }),
  })
  const [trackingUntil, setTrackingUntil] = useState('')
  const [startedFor, setStartedFor] = useState('')
  const [stopped, setStopped] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const secondsLeft = trackingSecondsLeft(trackingUntil, now)
  const active = startedFor === vehicleId && vehicleId !== '' && !stopped && secondsLeft > 0

  // Тикает раз в секунду, пока идёт слежение, — иначе обратный отсчёт в
  // бейдже застыл бы до следующего опроса раз в 10 с.
  useEffect(() => {
    if (!active) return
    const tick = setInterval(() => {
      setNow(Date.now())
    }, 1000)
    return () => clearInterval(tick)
  }, [active])

  const device = useQuery({
    queryKey: deviceQK.gpsRefresh(vehicleId),
    queryFn: () => fetchScoutDevice(vehicleId),
    enabled: active,
    refetchInterval: active ? LOCATE_TRACK_POLL_MS : false,
    retry: false,
  })
  const trail = useVehicleTrail(vehicleId, active, LOCATE_TRACK_TRAIL_LIMIT, {
    refetchInterval: active ? LOCATE_TRACK_POLL_MS : false,
  })

  return {
    start: () => {
      setStopped(false)
      locate.mutate(undefined, {
        onSuccess: (out) => {
          setStartedFor(vehicleId)
          setTrackingUntil(out.trackingUntil)
          setNow(Date.now())
        },
      })
    },
    starting: locate.isPending,
    error: locate.isError ? locateErrorText(locate.error) : null,
    active,
    secondsLeft,
    satellites: device.data?.satellites ?? 0,
    pointAgoText: formatPointAge(device.dataUpdatedAt, now),
    trail: trail.data,
    stop: () => {
      setStopped(true)
    },
  }
}
